import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { AxiosError, AxiosResponse } from 'axios';
import Drawer from 'components/drawer';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { usePost } from 'hooks/use-post';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ErrorMessage } from 'types/shared/error-message.type';
import { skipManualRecommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/update-ofd-settings';
import { useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { SkipManualRecommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/skip-manual-recommendation.type';

import Tooltip from 'components/tooltip';
import { Minus } from 'lucide-react';
import { Recommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/recommendation-assortment.type';

type Props = {
  recommendation: Recommendation;
};
const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';
const OFD_LIST_ID = 'orderFeed.ofdMySupplyPlan.orders.list';

const ModalTitle: React.FC = ({}) => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={3}>
      <Box>
        <Typography fontWeight={'bold'}>
          {intl.formatMessage(
            { id: `${PAGE_NAME_ID}.modals.skippedDialog.title` },
            {
              type: 'Manual',
            }
          )}
        </Typography>
      </Box>
      <IconButton sx={{ backgroundColor: theme.palette.darkBlue.o25, cursor: 'unset' }} disableRipple disableTouchRipple size="small">
        <Minus color="black" size={17} />
      </IconButton>
    </Box>
  );
};

const ManualRecommendationSkipDialog: React.FC<Props> = ({ recommendation }) => {
  const intl = useIntl();
  const params = useParams<OFD2Params>();

  const { orderSettingsQuery } = useOrdersState();

  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();

  const manualSkip = usePost<AxiosResponse, SkipManualRecommendation, AxiosError<ErrorMessage[]>>(
    (payload) => skipManualRecommendation(payload, params.globalSiteId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      [['getTimeline'], ['getOfdSettings'], ['getOrderAssortment'], ['getCustomerAssortment'], ['getRecommendationAssortment']].forEach(
        (queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        }
      );
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error, errorMsg: error.response?.data },
      });
    }
  );

  return (
    <Drawer
      title={<ModalTitle />}
      button={
        <Button size="small" variant="outlined" disabled={orderSettingsQuery.data?.isUnderMaintenance}>
          <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.modals.confirmedDialog.skipRecommendation.tooltipLabel` })}>
            <FormattedMessage id={`${OFD_LIST_ID}.unskipBtn`} />
          </Tooltip>
        </Button>
      }>
      <Box display={'flex'} flexDirection={'column'} width={600} gap={4}>
        <Typography fontWeight={500} textAlign={'center'}>
          <FormattedMessage
            id={`${PAGE_NAME_ID}.modals.confirmedDialog.skipRecommendation.unSkipRecommendationMessage`}
            values={{ date: dayjs(recommendation.dispatch).format('DD MMM') }}
          />
        </Typography>
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            startIcon={manualSkip.isPending ? <CircularProgress color="inherit" size={18} /> : null}
            variant="outlined"
            disabled={manualSkip.isPending}
            onClick={() => manualSkip.mutate({ skip: false, dispatchDate: recommendation.dispatch })}>
            <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.unSkipButton`} />
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ManualRecommendationSkipDialog;
